import { LogOut } from 'lucide-react'
import * as env from '@/env'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'

import { useChatStore } from '@/state/chat-store'
import { getColorSet } from '@/theme/color'
import { useUserStore } from '@/state/user-store'
import { ChannelType } from '../../../../shared-src/channel'
import { ChannelMember } from './channel-member'
import { ChannelDetailsHeader } from './channel-details-header'
import { leaveGroup } from './feathers-client'
import { useOnboardingBannerState } from '@/state/use-onboarding-banner-state'

interface ChannelDetailsSidebarProps {
	isOpen: boolean
	onClose: () => void
}

export function ChannelDetailsSidebar(props: ChannelDetailsSidebarProps) {
	const [isLeaving, setIsLeaving] = useState(false)
	const [dialogOpen, setDialogOpen] = useState(false)

	const channel = useChatStore(state => state.activeChannel)
	const user = useUserStore(state => state.user)
	const { isVisible: onboardingBannerVisible } = useOnboardingBannerState()

	if (!channel)
		return null

	const colorSet = getColorSet({ senderColor: user?.color })
	const channelType = ChannelType.fromChannelId(channel.id)
	const isGroupChannel = channelType === ChannelType.hangaroundGroup

	const devBannerVisible = !env.isProduction

	let topPadding = ''
	if (devBannerVisible && onboardingBannerVisible)
		topPadding = 'pt-20'
	else if (devBannerVisible)
		topPadding = 'pt-12'
	else if (onboardingBannerVisible)
		topPadding = 'pt-8'

	const handleLeaveGroup = async () => {
		setIsLeaving(true)
		try {
			await leaveGroup(channel.id)
			setDialogOpen(false)
			props.onClose()
		} catch (error) {
			console.error('Failed to leave group:', error)
		} finally {
			setIsLeaving(false)
		}
	}

	return (
		<div
			className={`
				fixed md:absolute inset-y-0 right-0 z-30 w-64 bg-white border-l transform transition-transform duration-200 ease-in-out
				${props.isOpen ? 'translate-x-0' : 'translate-x-full'}
				${topPadding}
			`}
			style={{ borderColor: colorSet.border }}
		>
			<div className="h-full flex flex-col">
				<ChannelDetailsHeader onClose={props.onClose} />

				<div className="flex-1 overflow-y-auto">
					{/* <p className="text-gray-700 mb-4 font-semibold">
						{channel.details?.description}
					</p> */}
					<p className="text-sm m-2">{channel.details?.description}</p>
					{env.isDevelopment && <p className="text-sm m-2">ChannelId: {channel.id}</p>}

					{channel.showsUserList && (() => {
						//	Pass the full member list to each row so display names can be
						//	disambiguated with a userId suffix when two members share a name.
						//	Includes self — self counts as a potential name-collider.
						const allMembers = Object.values(channel.details?.membersByUserId ?? {})
						return (
							<div className="flex flex-col gap-0">
								{Object.entries(channel.details?.membersByUserId ?? {}).map(
									([userId, member]) => {
										if (userId === user?.userId)
											return null
										return (<ChannelMember key={userId} member={member} allMembers={allMembers} />)
									}
								)}
							</div>
						)
					})()}
				</div>

				{isGroupChannel && (
					<div className="p-4 border-t" style={{ borderColor: colorSet.border }}>
						<ConfirmationDialog
							open={dialogOpen}
							onOpenChange={setDialogOpen}
							title="Leave Group"
							description="Are you sure you want to leave this group? You will not be able to re-join."
							confirmText={isLeaving ? 'Leaving...' : 'Leave Group'}
							onConfirm={handleLeaveGroup}
							isLoading={isLeaving}
							trigger={
								<Button variant="destructive" size="sm" className="w-full">
									<LogOut className="mr-2 h-4 w-4" />
									Leave Group
								</Button>
							}
						/>
					</div>
				)}
			</div>
		</div>
	)
} 